import React, { useEffect, useMemo, useState } from 'react';
import { Eye } from 'lucide-react';
import api from '@/lib/axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { matchesFreeText } from '@/components/search/SearchBuilder';

function relativeGroup(iso) {
  if (!iso) return 'Ohne Datum';
  const d = new Date(iso);
  const now = new Date();
  const diffDays = Math.floor((now - d) / (1000 * 60 * 60 * 24));
  if (diffDays <= 0) return 'Heute';
  if (diffDays === 1) return 'Gestern';
  if (diffDays < 7) return `Vor ${diffDays} Tagen`;
  if (diffDays < 31) return 'Vor einem Monat';
  if (diffDays < 365) return `Vor ${Math.floor(diffDays / 30)} Monaten`;
  return `Vor ${Math.floor(diffDays / 365)} Jahr(en)`;
}

function fmtWhen(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${String(d.getFullYear()).slice(2)} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/**
 * iMikel-style Änderungsprotokoll for a Belegung (eye icon).
 */
export default function RecordHistoryDialog({
  open,
  onOpenChange,
  enrollmentId,
  title = 'Änderungsprotokoll',
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!open || !enrollmentId) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await api.get(`/enrollments/${enrollmentId}/protocol`);
        if (!cancelled) setRows(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) {
          setRows([]);
          setError(e.response?.data?.detail || 'Protokoll konnte nicht geladen werden');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, enrollmentId]);

  const groups = useMemo(() => {
    const visible = rows.filter((r) => matchesFreeText(r, query));
    const map = new Map();
    for (const r of visible) {
      const g = relativeGroup(r.created_at);
      if (!map.has(g)) map.set(g, []);
      map.get(g).push(r);
    }
    return [...map.entries()];
  }, [rows, query]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col p-0 gap-0"
        data-testid="record-history-dialog"
      >
        {/* Kein eigenes X: DialogContent bringt oben rechts schon eines mit.
            pr-12 hält den Platz dafür frei. */}
        <DialogHeader className="px-4 py-3 pr-12 border-b border-slate-200 flex-row items-center space-y-0">
          <DialogTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <Eye className="w-4 h-4 text-brand-red" />
            {title}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-4 py-3">
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Protokoll durchsuchen…"
            className="w-full mb-3 h-8 text-xs border border-slate-200 rounded px-2"
            data-testid="record-history-search"
          />
          {loading && (
            <p className="text-sm text-slate-500 py-8 text-center">Lade Protokoll…</p>
          )}
          {error && !loading && (
            <p className="text-sm text-rose-600 py-4">{error}</p>
          )}
          {!loading && !error && rows.length === 0 && (
            <p className="text-sm text-slate-500 py-8 text-center">
              Noch keine Änderungen protokolliert. Speichern Sie eine Belegung, um den Verlauf zu starten.
            </p>
          )}
          {!loading && groups.map(([groupLabel, items]) => (
            <div key={groupLabel} className="mb-4">
              <div className="sticky top-0 z-10 bg-slate-100 text-slate-600 text-xs font-semibold uppercase tracking-wide px-2 py-1.5 rounded">
                {groupLabel}
              </div>
              {(() => {
                const bySection = new Map();
                for (const it of items) {
                  const s = it.section || 'Änderungen';
                  if (!bySection.has(s)) bySection.set(s, []);
                  bySection.get(s).push(it);
                }
                return [...bySection.entries()].map(([section, sectionRows]) => (
                  <div key={section} className="mt-2">
                    <p className="text-[11px] font-medium text-brand-red px-1 mb-1">{section}</p>
                    <div className="overflow-x-auto border border-slate-200 rounded-lg">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-50 text-slate-500">
                          <tr>
                            <th className="text-left font-medium px-2 py-1.5">Feld</th>
                            <th className="text-left font-medium px-2 py-1.5">Bisher</th>
                            <th className="text-left font-medium px-2 py-1.5">Neu</th>
                            <th className="text-left font-medium px-2 py-1.5">Benutzer:in</th>
                            <th className="text-left font-medium px-2 py-1.5">Datum</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sectionRows.map((r) => (
                            <tr key={r.id} className="border-t border-slate-100 hover:bg-amber-50/40">
                              <td className="px-2 py-1.5 font-medium text-slate-800">
                                {r.label === 'NEUEINGABE' || r.field === '_create' ? (
                                  <span className="text-brand-red">NEUEINGABE</span>
                                ) : r.label}
                              </td>
                              <td className="px-2 py-1.5 text-slate-500">{r.old_value || '—'}</td>
                              <td className="px-2 py-1.5 text-slate-800">{r.new_value || '—'}</td>
                              <td className="px-2 py-1.5 text-slate-600">{r.actor_name || '—'}</td>
                              <td className="px-2 py-1.5 text-slate-500 whitespace-nowrap">{fmtWhen(r.created_at)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ));
              })()}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function HistoryEyeButton({ onClick, disabled, className = '', title = 'Änderungsprotokoll' }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`p-1.5 rounded text-slate-500 hover:text-brand-red hover:bg-brand-red/5 disabled:opacity-40 disabled:pointer-events-none ${className}`}
      data-testid="record-history-eye-btn"
    >
      <Eye className="w-4 h-4" />
    </button>
  );
}
